/**
 * PlanExplorerDialog — full-screen, Stripe-mirrored two-column layout.
 * Left: plan summary + order preview + trust signals.
 * Right: plan selector + CTA.
 * Defaults to monthly billing. Contextual trial framing.
 */

import { AMIR_CALL_URL } from "@/lib/constants/support"
import { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Dialog, DialogPortal } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SegmentedControl } from "@/components/shared/layout";
import { PLAN_TESTIMONIALS } from "@/data/testimonials";
import { cn } from "@/lib/utils";
import { PRICING_PLANS, getPaymentLink, getPlanDisplayName } from "@/lib/constants/pricing";
import { useSubscription } from "@/lib/contexts/SubscriptionContext";
import { useAuth } from "@/lib/contexts/AuthContext";
import { getPortalLink } from "@/lib/api/billing";
import { sendMobileSetupLink } from "@/lib/api/api";
import {
  markTrialActivating,
  markOnboardingCheckout,
} from "@/lib/utils/trialActivation";
import { track } from "@/lib/utils/track";
import { buildPricingModalLimitHeader } from "@/components/shared/plan-usage";
import { logPaywallShown, usePaywallScope } from "@/lib/observability/paywall";
import { useBillingScope } from "@/lib/observability/billing";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Tick01Icon,
  Cancel01Icon,
  Shield01Icon,
  SmartPhone01Icon,
  Mail01Icon,
  Loading01Icon,
} from "@hugeicons/core-free-icons";

import amirProfile from "@/assets/profiles/amir-profile.jpeg";
import HumblyticsLogo from "@/assets/humblytics-web-app-logo.svg";

/** Single source of truth — outcome-shaped proof for the payment step. */
const [PROOF, ...SUPPORTING_PROOF] = PLAN_TESTIMONIALS;

interface PlanExplorerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPlanId?: string;
  /** When true, the close button is hidden and Escape/outside-click are blocked */
  persistent?: boolean;
  /**
   * "onboarding" — renders post-OTP copy for a brand-new user who has not yet
   * added a site. Adjusts the left-panel headline, skips the billing-portal
   * branch, and sets the markOnboardingCheckout flag before redirecting to Stripe.
   */
  context?: "onboarding";
  /** Plan-limit reason from `openPricingModal({ reason })` — contextual header. */
  limitReason?: string | null;
}

const ENTERPRISE_CONTACT_URL = "https://humblytics.com/request-demo";

export default function PlanExplorerDialog({
  open,
  onOpenChange,
  currentPlanId,
  persistent = false,
  context,
  limitReason,
}: PlanExplorerDialogProps) {
  const isOnboardingContext = context === "onboarding";
  const { usedTrial, hasActiveSubscription, hasActiveTrial, currentPlan, planName, isReadOnly, stripeSubscriptionId } = useSubscription();
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const paywallScope = usePaywallScope();
  const billingScope = useBillingScope();
  const [isAnnual, setIsAnnual] = useState(false); // default monthly
  const [selectedPlanId, setSelectedPlanId] = useState("business");
  const [isLoading, setIsLoading] = useState(false);
  const [mountKey, setMountKey] = useState(0);
  const isMobile = useMemo(() => /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent), []);
  const [mobileLinkSent, setMobileLinkSent] = useState(false);
  const [isSendingMobileLink, setIsSendingMobileLink] = useState(false);

  // Increment key each time dialog opens so framer-motion always runs from initial state.
  // Paywall presentation is logged on the same false→true transition.
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setMountKey(k => k + 1);
      logPaywallShown({
        surface: "plan_explorer",
        trigger: isOnboardingContext ? "onboarding_post_otp" : persistent ? "forced_paywall" : "manual",
        persistent,
        pathname: location.pathname,
        scope: paywallScope,
      });
    }
    prevOpen.current = open;
  }, [open, isOnboardingContext, location.pathname, paywallScope, persistent]);

  // Contextual framing: trial users see "keep your data flowing, add payment method"
  const isTrialContext = hasActiveTrial && !hasActiveSubscription;

  const selectedPlan = PRICING_PLANS.find((p) => p.id === selectedPlanId) ?? PRICING_PLANS[1];
  const displayPlanName = getPlanDisplayName(currentPlan, planName);
  // A canceled (read-only) subscription is an ended plan, not a usage cap — the
  // "you've used all {noun}" header would misread the state, so suppress it.
  const limitHeader = isReadOnly ? null : buildPricingModalLimitHeader(limitReason, displayPlanName);
  const isPlusLegacy = currentPlanId === "plus_legacy";

  const openBillingPortal = async () => {
    if (!token) return;
    let newWindow: Window | null = null;
    try {
      newWindow = window.open("", "_blank");
      const priceId = isAnnual
        ? selectedPlan.stripePriceIds.annual
        : selectedPlan.stripePriceIds.monthly;
      const { url } = await getPortalLink(token, {
        flow: "update",
        subscriptionId: stripeSubscriptionId,
        priceId,
        scope: billingScope,
      });
      if (url) {
        if (newWindow) newWindow.location.href = url;
        else window.location.href = url;
      } else {
        newWindow?.close();
      }
    } catch {
      newWindow?.close();
    }
  };

  const handleSelectPlan = async () => {
    setIsLoading(true);
    // Skip billing portal in onboarding context — new users can't have a subscription.
    if (hasActiveSubscription && !isOnboardingContext) {
      await openBillingPortal();
      setIsLoading(false);
      onOpenChange(false);
      return;
    }
    const paymentLink = getPaymentLink(selectedPlan, isAnnual, usedTrial, user?.email, user?.userId);
    track("stripe_checkout_initiated", token, {
      plan: selectedPlan.id,
      billing: isAnnual ? "annual" : "monthly",
      is_trial: !usedTrial,
      trigger: isOnboardingContext ? "onboarding_post_otp" : persistent ? "forced_paywall" : "manual",
    });
    markTrialActivating();
    if (isOnboardingContext) {
      markOnboardingCheckout();
    }
    window.location.href = paymentLink;
  };

  const handleSendMobileLink = async () => {
    if (!token) return;
    setIsSendingMobileLink(true);
    const continueUrl = `${window.location.origin}/auth/token`;
    try {
      await sendMobileSetupLink(continueUrl, token);
      setMobileLinkSent(true);
      track("mobile_setup_link_sent", token, { step: "plan" });
    } catch {
      // silent — best-effort
    } finally {
      setIsSendingMobileLink(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (persistent && !next) return;
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <motion.div
          key={`overlay-${mountKey}`}
          className="fixed inset-0 z-50 bg-[#171717]/45 dark:bg-black/60"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0 }}
        />
        <DialogPrimitive.Content className="fixed inset-0 z-[51] focus:outline-none" asChild>
          <motion.div
            key={`content-${mountKey}`}
            className="flex h-full flex-col overflow-y-auto bg-card lg:flex-row lg:overflow-hidden"
            initial={{ opacity: 1, scaleX: 1, scaleY: 1, y: 0 }}
            animate={{ opacity: 1, scaleX: 1, scaleY: 1, y: 0 }}
            exit={{ opacity: 0, scaleX: 0.5, scaleY: 0.5, y: '25vh' }}
            style={{ transformOrigin: 'center bottom' }}
            transition={{ duration: 0 }}
          >
            {/* ── Left: plan summary ──
                On <lg this renders a compact version on top (trust badges +
                testimonial are hidden) and the page scrolls as one column. */}
            {/* Order flips on mobile: 31% of traffic to this gate is mobile and
                avg scroll depth is 24%, so leading with the reassurance panel
                buried the actual price decision below the fold. Plans first on
                small screens; reassurance reads as supporting detail underneath. */}
            <div className="order-2 flex flex-col border-b border-border/40 bg-surface lg:order-1 lg:w-[44%] lg:overflow-y-auto lg:border-b-0 lg:border-r xl:w-[40%]">
              {/* Content hugs the centre seam rather than the outer edge, the way
                  Stripe Checkout balances its two columns. Centring each column
                  independently left a dead gutter down the middle on wide screens. */}
              <div className="mx-auto w-full max-w-md flex-1 px-8 py-6 sm:px-12 lg:ml-auto lg:mr-0 lg:flex lg:flex-col lg:justify-center lg:py-10">

                {/* Logo — matches the /auth lockup. Hidden on mobile, where the
                    right column carries it above the plan selector instead. */}
                <div className="mb-6 hidden items-center gap-1.5 lg:mb-10 lg:flex">
                  <img src={HumblyticsLogo} alt="Humblytics" className="h-5 w-auto" />
                  <span className="text-sm font-medium tracking-tight text-foreground">Humblytics</span>
                </div>

                {/* Left panel headline — always explains why this is appearing */}
                {hasActiveSubscription ? (
                  <div className="xl:mb-6 lg:mb-6">
                    <p className="mb-3 text-xs font-semibold text-muted-foreground">Subscription</p>
                    <h2 className="mb-3 text-[22px] font-medium leading-tight tracking-tight text-foreground">Update your plan</h2>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      Changes take effect immediately. Upgrades are pro-rated; downgrades apply at the next billing cycle.
                    </p>
                    {isPlusLegacy && (
                      <p className="mt-3 text-xs text-muted-foreground">
                        You're currently on Plus (Legacy).
                      </p>
                    )}
                    {limitHeader && (
                      <p className="mt-3 rounded-lg border border-warning/30 bg-warning-tint px-3.5 py-2.5 text-sm text-foreground">
                        {limitHeader}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="xl:mb-6 lg:mb-6">
                    <p className="mb-3 text-xs font-semibold text-primary">Start your free trial</p>
                    <h2 className="mb-3 text-[22px] font-medium leading-tight tracking-tight text-foreground">
                      {isOnboardingContext
                        ? "Start your 14-day free trial, $0 today"
                        : "Pick a plan to keep collecting data"}
                    </h2>
                    {/* Revenue-led, not a feature list. Positioning.md: "Lead with
                        revenue attribution + A/B testing outcomes." */}
                    <p className="mb-5 text-sm leading-relaxed text-muted-foreground">
                      {isOnboardingContext
                        ? "See which campaigns and page variants actually make money, and kill the ones that don't."
                        : "Humblytics is already tracking your site. Pick a plan to keep data flowing and insights live."}
                    </p>
                    {/* Card requirement is disclosed HERE, before Stripe. 33% of users who
                        clicked through were dropping at the card form. The surprise was the
                        leak, not the requirement. Stating it up front pre-qualifies the click. */}
                    <div className="mb-4 rounded-lg border border-border/40 bg-card px-4 py-3.5">
                      <p className="mb-1 text-sm font-medium text-foreground">$0 today &middot; card required</p>
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        Stripe collects your card on the next screen to start the trial. Nothing is charged until day 14. Cancel before then and you pay nothing.
                      </p>
                    </div>
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      {isOnboardingContext
                        ? "Next up: add your site and connect your tracker. Takes under 2 minutes."
                        : "Every plan includes heatmaps, A/B tests, funnels, revenue tracking, and AI insights."}
                    </p>
                    {isPlusLegacy && (
                      <p className="mt-3 text-xs text-muted-foreground">
                        You're currently on Plus (Legacy).
                      </p>
                    )}
                    {limitHeader && (
                      <p className="mt-3 rounded-lg border border-warning/30 bg-warning-tint px-3.5 py-2.5 text-sm text-foreground">
                        {limitHeader}
                      </p>
                    )}
                  </div>
                )}

                {/* Trust badges — desktop only. Each states something the copy above
                    does NOT already say; repeating "no charge" a third time reads as
                    anxiety rather than reassurance. */}
                <div className="hidden space-y-2.5 lg:block xl:block">
                  {[
                    "Full access to every feature during the trial",
                    "Cancel anytime, no questions asked",
                    "Your data is safe and always accessible",
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2.5 text-xs text-muted-foreground">
                      <HugeiconsIcon icon={Shield01Icon} size={13} className="shrink-0 text-success dark:text-success" />
                      {item}
                    </div>
                  ))}
                </div>

                {/* Proof — one full quote, then a compact stat strip for the rest.
                    Breadth of evidence matters more than depth at a payment step,
                    and the strip adds two more customers for ~40px of height. */}
                <div className="mt-8 hidden rounded-lg border border-border/30 bg-card p-4 lg:block">
                  <div className="mb-2 flex items-baseline gap-2">
                    <p className="text-sm font-medium leading-none tabular-nums text-foreground">
                      {PROOF.stat}
                    </p>
                    <p className="text-xs font-medium leading-none text-muted-foreground">
                      {PROOF.statLabel}
                    </p>
                  </div>
                  <p className="mb-3 text-xs leading-relaxed text-muted-foreground">
                    &ldquo;{PROOF.quote}&rdquo;
                  </p>
                  <div className="flex items-center gap-2.5 border-t border-border/20 pt-2.5">
                    <img src={PROOF.avatar} alt="" aria-hidden className="size-7 rounded-full object-cover ring-1 ring-border/40" />
                    <div>
                      <p className="text-xs font-medium text-foreground">{PROOF.name}</p>
                      <p className="text-xs text-muted-foreground">{PROOF.role}</p>
                    </div>
                  </div>

                  <div className="mt-3 space-y-2 border-t border-border/20 pt-3">
                    {SUPPORTING_PROOF.map((t) => (
                      <div key={t.id} className="flex items-center gap-2.5">
                        <img src={t.avatar} alt="" aria-hidden className="size-5 shrink-0 rounded-full object-cover ring-1 ring-border/40" />
                        <p className="text-xs font-medium tabular-nums text-foreground">{t.stat}</p>
                        <p className="min-w-0 truncate text-xs text-muted-foreground">{t.role}</p>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>

            {/* ── Right: plan selector ── (conversion surface) */}
            <div className="order-1 flex flex-col bg-card lg:order-2 lg:flex-1 lg:overflow-y-auto">
              <div className="mx-auto w-full max-w-lg flex-1 px-8 py-10 sm:px-12 sm:py-14 lg:ml-0 lg:mr-auto lg:flex lg:flex-col lg:justify-center">

                {/* Mobile-only lockup — the left panel's logo is hidden below lg */}
                <div className="mb-6 flex items-center gap-1.5 lg:hidden">
                  <img src={HumblyticsLogo} alt="Humblytics" className="h-5 w-auto" />
                  <span className="text-sm font-medium tracking-tight text-foreground">Humblytics</span>
                </div>

                {/* Header */}
                <div className="mb-8 flex items-center justify-between gap-3">
                  <h3 className="text-base font-medium text-foreground">
                    {isTrialContext ? "Choose your plan" : hasActiveSubscription ? "Update plan" : "Select a plan"}
                  </h3>
                  <SegmentedControl
                    options={[
                      { value: "monthly", label: "Monthly" },
                      { value: "annual", label: "Annual · -18%" },
                    ]}
                    value={isAnnual ? "annual" : "monthly"}
                    onChange={(v) => setIsAnnual(v === "annual")}
                  />
                </div>

                {/* "Pay for traffic, not features" is a locked unique attribute in
                    Positioning.md, but the per-plan limits below read as feature
                    gating. Saying it outright removes the "what do I lose on Plus"
                    hesitation before it starts. */}
                {!usedTrial && (
                  <p className="-mt-4 mb-4 text-xs leading-relaxed text-muted-foreground">
                    Every plan includes every feature: A/B testing, heatmaps, funnels,
                    revenue attribution, and the Agent API. Plans differ by volume, not tools.
                  </p>
                )}

                {/* Plan cards */}
                <div className="space-y-3 mb-6">
                  {PRICING_PLANS.filter((p) => p.id !== "plus").map((plan) => {
                    const isSelected = selectedPlanId === plan.id;
                    const price = isAnnual ? plan.annualPrice : plan.monthlyPrice;
                    const isCurrent = plan.id === currentPlanId;

                    return (
                      <button
                        key={plan.id}
                        type="button"
                        onClick={() => setSelectedPlanId(plan.id)}
                        className={cn(
                          "w-full rounded-lg border px-5 py-4 text-left transition-colors",
                          isSelected
                            ? "border-primary bg-primary/[0.02]"
                            : "border-border/30 bg-card hover:border-border/60 hover:bg-muted/30"
                        )}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="mb-0.5 flex items-center gap-2">
                              <span className="text-sm font-medium text-foreground">{plan.name}</span>
                              {plan.badge && (
                                <Badge variant="secondary" className="rounded-full px-1.5 py-0 text-xs font-semibold">
                                  {plan.badge}
                                </Badge>
                              )}
                              {isCurrent && (
                                <Badge variant="outline" className="px-1.5 py-0 text-xs">Current</Badge>
                              )}
                            </div>
                            <p className="line-clamp-2 pr-4 text-xs leading-snug text-muted-foreground">
                              {plan.description}
                            </p>
                            {/* Always-visible differentiator so plans stay comparable
                                without having to select each one in turn. */}
                            {!isSelected && (
                              <p className="mt-1.5 text-xs tabular-nums text-muted-foreground/80">
                                {[plan.features[0], plan.features[2]].filter(Boolean).join(" · ")}
                              </p>
                            )}
                          </div>
                          <div className="shrink-0 text-right">
                            {!usedTrial ? (
                              <>
                                <div className="flex items-baseline justify-end gap-1.5">
                                  <span className="text-[22px] font-semibold tabular-nums text-foreground">$0</span>
                                  <span className="text-xs text-muted-foreground">14 days</span>
                                </div>
                                <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                                  then {isAnnual && plan.annualTotal ? `$${plan.annualTotal.toLocaleString()}/yr` : `$${plan.monthlyPrice}/mo`}
                                </p>
                              </>
                            ) : (
                              <>
                                <div className="flex items-baseline justify-end gap-1.5">
                                  <span className="text-[22px] font-semibold tabular-nums text-foreground">${price}</span>
                                  <span className="text-xs text-muted-foreground">/mo</span>
                                </div>
                                {isAnnual && plan.annualTotal && (
                                  <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">${plan.annualTotal.toLocaleString()}/yr</p>
                                )}
                              </>
                            )}
                          </div>
                        </div>

                        {/* Full feature list — expands for the selected plan */}
                        {isSelected && (
                          <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 border-t border-border/20 pt-3">
                            {plan.features.map((f) => (
                              <div key={f} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                <HugeiconsIcon icon={Tick01Icon} size={11} className="shrink-0 text-success dark:text-success" />
                                {f}
                              </div>
                            ))}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* CTA */}
                <Button
                  onClick={handleSelectPlan}
                  disabled={isLoading || (currentPlanId === selectedPlanId && !!currentPlanId && !hasActiveSubscription)}
                  className="h-11 w-full text-sm font-medium"
                >
                  {isLoading ? (
                    <HugeiconsIcon icon={Loading01Icon} size={18} className="animate-spin" />
                  ) : hasActiveSubscription ? (
                    "Update plan"
                  ) : !usedTrial ? (
                    // Names the reward, not the cost — the card disclosure lives in
                    // the left panel and the line below, so the button stays confident.
                    "Start free trial"
                  ) : (
                    "Get started"
                  )}
                </Button>

                {!hasActiveSubscription && (
                  <p className="mt-3 text-center text-xs leading-relaxed text-muted-foreground">
                    {isTrialContext
                      ? "Your 14-day trial continues, nothing is charged until it ends"
                      : "Next: Stripe collects your card. $0 today, then billed on day 14 unless you cancel."}
                  </p>
                )}

                {/* Enterprise */}
                <div className="mt-6 flex items-center justify-between rounded-lg border border-border/30 bg-muted/30 px-4 py-3">
                  <div>
                    <p className="text-xs font-medium text-foreground">Enterprise</p>
                    <p className="text-xs text-muted-foreground">Unlimited events, team members, and priority support</p>
                  </div>
                  <a
                    href={ENTERPRISE_CONTACT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-4 shrink-0 text-xs font-medium text-primary hover:text-primary/80"
                  >
                    Contact sales →
                  </a>
                </div>

                {/* Amir — alt is empty so a failed avatar collapses instead of
                    spilling "Amir" text across the row. */}
                <div className="mt-4 flex items-center gap-3 rounded-lg border border-border/30 bg-muted/30 px-4 py-3">
                  <img src={amirProfile} alt="" aria-hidden className="size-8 shrink-0 rounded-full object-cover ring-1 ring-border/40" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-foreground">Free onboarding call with Amir</p>
                    <p className="text-xs text-muted-foreground">25-min walkthrough · included in your trial</p>
                  </div>
                  <a
                    href={AMIR_CALL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 text-xs font-medium text-primary hover:text-primary/80"
                  >
                    Book →
                  </a>
                </div>

                {/* Logout escape hatch — only in the persistent (forced) gate */}
                {persistent && (
                  <div className="mt-8 text-center">
                    <button
                      type="button"
                      onClick={() => {
                        track("paywall_logout_clicked", token, { trigger: "forced_paywall" });
                        logout();
                        navigate("/auth");
                      }}
                      className="text-[13px] text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground"
                    >
                      Not ready? Log out
                    </button>
                  </div>
                )}

              </div>

              {/* Mobile banner — onboarding context only, pinned to bottom */}
              {isOnboardingContext && isMobile && (
                <div className="px-8 pb-6 sm:px-12 max-w-lg mx-auto w-full">
                  <div className="rounded-lg border border-warning/40 bg-warning-tint px-4 py-3">
                    <div className="flex items-start gap-3">
                      <HugeiconsIcon icon={SmartPhone01Icon} size={16} className="text-warning shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-warning mb-0.5">On mobile?</p>
                        <p className="text-xs text-warning/80 leading-snug mb-2">
                          Finish setup on your desktop to add your site and install the tracker.
                        </p>
                        {mobileLinkSent ? (
                          <p className="text-xs text-warning font-medium flex items-center gap-1.5">
                            <HugeiconsIcon icon={Tick01Icon} size={11} className="text-success" /> Link sent. Check your inbox.
                          </p>
                        ) : (
                          <button
                            type="button"
                            onClick={handleSendMobileLink}
                            disabled={isSendingMobileLink}
                            className="text-xs font-semibold text-warning underline underline-offset-2 hover:no-underline disabled:opacity-60 flex items-center gap-1.5"
                          >
                            {isSendingMobileLink ? (
                              <><HugeiconsIcon icon={Loading01Icon} size={11} className="animate-spin" /> Sending...</>
                            ) : (
                              <><HugeiconsIcon icon={Mail01Icon} size={11} /> Send me a link to continue on desktop</>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          {/* Close button — hidden when persistent */}
          {!persistent && (
            <button
              onClick={() => onOpenChange(false)}
              className="absolute right-4 top-4 z-50 rounded-full bg-muted p-1.5 transition-colors hover:bg-muted/70"
            >
              <HugeiconsIcon icon={Cancel01Icon} size={16} className="text-muted-foreground" />
            </button>
          )}
          </motion.div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
